import type { components } from '@fluencelabs/vodopad-admin-api'
import { Form, Input, message, Modal, Radio, Select, Spin } from 'antd'
import { useState } from 'react'

import { useAvailablePublicIps } from '@/hooks/useAvailablePublicIps'
import { usePublicIpActions } from '@/hooks/usePublicIpActions'

type UserVmDto = components['schemas']['AdminUserVmDto']
type IpAddressType = components['schemas']['IpAddressType']
type UserPublicIpDto = components['schemas']['UserPublicIpDto']

type Mode = 'random' | 'specific' | 'create'

interface Props {
  vm: UserVmDto
  open: boolean
  onClose: () => void
  onAttached: () => void
}

export function AttachPublicIpModal({ vm, open, onClose, onAttached }: Props) {
  const { attach, createAndAttach } = usePublicIpActions()
  const { available, isLoading, refetch } = useAvailablePublicIps({
    userId: vm.userId,
    clusterId: vm.clusterId,
    enabled: open,
  })
  const [mode, setMode] = useState<Mode>('random')
  const [selectedId, setSelectedId] = useState<string>()
  const [name, setName] = useState('')
  const [addressType, setAddressType] = useState<IpAddressType>('V4')
  const [submitting, setSubmitting] = useState(false)

  const handleClose = () => {
    setMode('random')
    setSelectedId(undefined)
    setName('')
    setAddressType('V4')
    onClose()
  }

  const submitExisting = async () => {
    const publicIpId =
      mode === 'specific'
        ? selectedId
        : available[Math.floor(Math.random() * available.length)]?.id
    if (!publicIpId) {
      void message.error('Select a public IP to attach')
      return
    }
    setSubmitting(true)
    try {
      await attach(vm.id, publicIpId)
      onAttached()
      handleClose()
    } catch (error) {
      void refetch()
      if (error instanceof Error) void message.error(error.message)
    } finally {
      setSubmitting(false)
    }
  }

  // Provisioning takes seconds — close the modal and run it in the background
  // with a progress toast so the operator isn't blocked.
  const submitCreate = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      void message.error('Enter a name for the new IP')
      return
    }
    const input = { name: trimmed, addressType }
    handleClose()
    const hide = message.loading('Creating & provisioning public IP…', 0)
    void createAndAttach(vm, input)
      .then(({ attached }) => {
        hide()
        if (!attached) {
          void message.info(
            'IP created — still provisioning. Attach it from the pool once it is ready.',
          )
        }
        onAttached()
      })
      .catch((error) => {
        hide()
        if (error instanceof Error) void message.error(error.message)
      })
  }

  const okDisabled =
    (mode === 'random' && (isLoading || available.length === 0)) ||
    (mode === 'specific' && !selectedId) ||
    (mode === 'create' && !name.trim())

  return (
    <Modal
      title={`Attach Public IP · VM ${vm.name}`}
      open={open}
      onOk={() => (mode === 'create' ? submitCreate() : void submitExisting())}
      onCancel={handleClose}
      okText={mode === 'create' ? 'Create & Attach' : 'Attach'}
      okButtonProps={{ disabled: okDisabled }}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item>
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
          >
            <Radio value="random">Random available IP</Radio>
            <Radio value="specific">Specific IP</Radio>
            <Radio value="create">Create new IP</Radio>
          </Radio.Group>
        </Form.Item>
        {mode === 'create' ? (
          <CreateFields
            name={name}
            addressType={addressType}
            onName={setName}
            onAddressType={setAddressType}
          />
        ) : (
          <ExistingFields
            mode={mode}
            isLoading={isLoading}
            available={available}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )}
      </Form>
    </Modal>
  )
}

function ExistingFields({
  mode,
  isLoading,
  available,
  selectedId,
  onSelect,
}: {
  mode: Mode
  isLoading: boolean
  available: UserPublicIpDto[]
  selectedId: string | undefined
  onSelect: (id: string) => void
}) {
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 16 }}>
        <Spin />
      </div>
    )
  }
  const note =
    available.length === 0
      ? 'No ready, unattached IPs for this user in this cluster — use “Create new IP”.'
      : `${available.length} available`
  if (mode === 'random') {
    return (
      <Form.Item label="Random pick">
        {available.length === 0
          ? note
          : `A random free IP from the ${available.length} available will be allocated — which one is known only after attaching.`}
      </Form.Item>
    )
  }
  return (
    <Form.Item label={`Public IP (${note})`}>
      <Select
        placeholder="Select a public IP"
        value={selectedId}
        onChange={onSelect}
        showSearch
        optionFilterProp="label"
        disabled={available.length === 0}
        options={available.map((ip) => ({
          value: ip.id,
          label: `${ip.name} — ${ip.address ?? 'pending'}`,
        }))}
      />
    </Form.Item>
  )
}

function CreateFields({
  name,
  addressType,
  onName,
  onAddressType,
}: {
  name: string
  addressType: IpAddressType
  onName: (v: string) => void
  onAddressType: (v: IpAddressType) => void
}) {
  return (
    <>
      <Form.Item label="Name" required>
        <Input
          placeholder="IP name"
          value={name}
          onChange={(e) => onName(e.target.value)}
        />
      </Form.Item>
      <Form.Item label="Address Type">
        <Select
          value={addressType}
          onChange={onAddressType}
          options={[
            { value: 'V4', label: 'IPv4' },
            { value: 'V6', label: 'IPv6' },
          ]}
        />
      </Form.Item>
    </>
  )
}
